import { items } from "@/api/items/useItemQuery";
import { KPI } from "@/api/kpi/useKPIQuery";
import { columnlist } from "@/constants/columnlist";
import { useModal } from "@/features/modal/ModalStackManager";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { Calendar, isCalendarDate } from "@/features/ui/Calendar";
import { GridTable } from "@/features/ui/PMS/GridTable";
import CustomTable from "@/features/ui/table/CustomTable";
import { DatetimeUtilMinute, getEndOfDate, getStartOfDate } from "@/utils/dateTimeUtil";
import timeUtil from "@/utils/timeUtil";
import { ActionIcon, Flex, Space, Text } from "@mantine/core";
import { ItemsGet200ResponseRowsInnerItemTypeEnum } from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";


const KPIDefect = Object.assign({}, {
    Header: Header,
    HeaderTitle: HeaderTitle,
    HeaderSubTitle: HeaderSubTitle,
    CustomTable: CustomTable,
})

export const KPIDefectPage = () => {
    const today = dayjs().subtract(1, 'day').startOf('day').toDate();
    const oneWeekAgo = dayjs().subtract(7, 'day').startOf('day').toDate();

    const [content, setContent] = useState<any>([]);
    const [selectList, setSelectList] = useState<Set<number>>(new Set());
    const [stateDate, setStateDate] = useState<[Date | null, Date | null]>([oneWeekAgo, today]);
    const [item, setItem] = useState<string | null>('');
    const [totalQuantity, setTotalQuantity] = useState<number>(0);
    const [totalDefectQuantity, setTotalDefectQuantity] = useState<number>(0);
    const [totalDefectRate, setTotalDefectRate] = useState<string>('');
    const { openModal } = useModal();

    const { data } = useQuery({
        ...items.get({
            query: {
                $and: [
                    { code: item }
                ]
            }
        }),
        enabled: !!item
    })

    const { data: defectData } = useQuery({
        ...KPI.defectRate({
            itemCode: item ?? '',
            scheduledAtFrom: getStartOfDate(stateDate[0]),
            scheduledAtTo: getEndOfDate(stateDate[1])
        }),
        enabled: !!item
    })

    const itemData = data?.data?.rows && data?.data?.rows[0]

    useEffect(() => {
        if (defectData?.data) {
            const transformedData = defectData.data.map((defect: any) => ({
                id: defect.id,
                productionPlanId: defect.productionPlanId,
                scheduledAt: defect.scheduledAt,
                operationName: defect.operationName,
                equipmentName: defect.equipmentName,
                itemCode: defect.itemCode,
                itemSpec: defect.itemSpec,
                workerName: defect.workerName,
                workEndTime: DatetimeUtilMinute(defect.workEndTime),
                workStartTime: DatetimeUtilMinute(defect.workStartTime),
                pauseDuration: defect.pauseDuration,
                totalQuantity: `${defect.totalQuantity} ${itemData?.unitText}`,
                goodQuantity: `${defect.goodQuantity} ${itemData?.unitText}`,
                defectQuantity: `${defect.defectQuantity} ${itemData?.unitText}`,
                createdAt: defect.createdAt,
                updatedAt: defect.updatedAt,
                defectRate: defect.defectRate,
            }));

            const totalQuantity = defectData?.data?.reduce((sum: number, defect: any) => sum + parseFloat(defect.totalQuantity), 0) ?? 0;
            const totalDefectQuantity = defectData?.data?.reduce((sum: number, defect: any) => sum + parseFloat(defect.defectQuantity), 0) ?? 0;

            // 평균 불량률 계산
            const averageDefectRate = (
                defectData.data.reduce((sum: number, defect: any) => sum + parseFloat(defect.defectRate), 0) / defectData.data.length
            ).toFixed(2);

            setTotalQuantity(totalQuantity);
            setTotalDefectQuantity(totalDefectQuantity);
            setTotalDefectRate(averageDefectRate);

            setContent(transformedData);
        } else {
            setContent([]);
        }
    }, [defectData]);


    const TableHeader = {
        machine: ['품목코드', '품명', '규격', '총 생산 수량', '총 불량 수량', '평균 불량률(%)'],
    }

    const BodyData = {
        machine: [{ '품목코드': itemData?.code ?? '-', '품명': itemData?.name, '규격': itemData?.spec, '총 생산 수량': totalQuantity, '총 불량 수량': totalDefectQuantity, '평균 불량률(%)': totalDefectRate === "NaN" ? '-' : totalDefectRate }],
    }

    return (
        <Flex gap="md" direction={'column'}>
            <KPIDefect.Header>
                <KPIDefect.HeaderTitle>품질 불량률(Q)</KPIDefect.HeaderTitle>
                <KPIDefect.HeaderSubTitle>불량수량 / 총 생산수량</KPIDefect.HeaderSubTitle>
            </KPIDefect.Header>
            <Flex justify={'space-between'} align={'center'}>
                <Space miw={'22rem'} />
                <Flex align={'center'}>
                    <Flex align="center" gap="sm" onClick={() => {
                        openModal(<Calendar dates={stateDate} type="range" isExclude={true} />, null, '날짜 선택')
                            .then((value) => {
                                if (isCalendarDate(value)) {
                                    setStateDate(value);
                                }
                            });
                    }}>
                        <Text size="2rem" fw="bold" style={{ cursor: "pointer" }}>
                            {timeUtil(stateDate[0]?.toISOString() ?? '')} ~ {timeUtil(stateDate[1]?.toISOString() ?? '')}
                        </Text>
                        <ActionIcon>
                            <IconCalendar size="2rem" />
                        </ActionIcon>
                    </Flex>
                </Flex>
                <ItemAutoComplete
                    placeholder="품목을 선택해주세요"
                    w="22rem"
                    size="lg"
                    getItemType={[ItemsGet200ResponseRowsInnerItemTypeEnum.PRODUCT, ItemsGet200ResponseRowsInnerItemTypeEnum.SEMI_PRODUCT]}
                    onChange={(value) => setItem(value)}
                />
            </Flex>

            <KPIDefect.CustomTable
                headers={TableHeader.machine}
                data={BodyData.machine}
            />
            <GridTable
                headerList={[
                    ...columnlist.defectTable
                ]}
                row={content}
                setRow={setContent}
                selectList={selectList}
                //@ts-ignore
                setSelectList={setSelectList}
                width={'100%'}
                height={750}
                resizable={true}
            // scrollEnd={(value) => {
            //     if(value){
            //         if(keyword !== undefined){
            //             if(searchPage.total_page > searchPage.current_page){
            //                 getPressDailyStatusSearchApi(keyword, searchPage.current_page+ 1)
            //             }
            //         }else{
            //             if(page.total_page > page.current_page){
            //                 getPressDailyStatusApi(page.current_page+ 1)
            //             }
            //         }
            //     }
            // }}
            />
        </Flex>
    )
}